import React, { PureComponent } from 'react'
import { isString, omit } from 'lodash'
import classnames from 'classnames'
import { DraggableBounds } from 'react-draggable'
import { FilterConfig, ResizeInfo } from 'types'
import Close from 'icons/Close'
import ColumnDragHandle, {
  Props as ColumnDragHandleProps
} from 'ColumnDragHandle'

import styles from './styles.module.css'

const shortId = require('shortid')

type Props = {
  columnKey: string
  resizable: boolean
  resizeBounds: DraggableBounds
  onResizeEnd: (resizeInfo: ResizeInfo) => void
  onResizeStart: (resizeInfo: ResizeInfo) => void
  onLabelClick: (event: any) => void
  style: object
  label: string
  className: string
  columnLabelStyle: {}
  filter: FilterConfig
  dragHandleIcon?: JSX.Element
  sortIcon?: JSX.Element
}

class ColumnHeader extends PureComponent<Props> {
  static defaultProps = {
    columnLabelStyle: {
      color: '#58595b',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      display: 'inline-block',
      maxWidth: '100%',
      overflow: 'hidden',
      textTransform: 'none'
    },
    filter: {
      disabled: false,
      type: '',
      onChange: () => {},
      options: [],
      placeholder: '',
      value: ''
    },
    label: '',
    onLabelClick: () => {},
    onResizeStart: () => {},
    onResizeEnd: () => {},
    resizable: false,
    style: {},
    sortIcon: null
  }

  static optionSorter = (a: any, b: any) => a.label.localeCompare(b.label)

  static makeOptions = (values: any[], sort = true) => {
    const normalized = [{ value: '', label: '', disabled: false }].concat(
      values.map((value) => {
        if (isString(value)) {
          return { value, label: value }
        }
        return value
      })
    )
    const options = sort
      ? normalized.sort(ColumnHeader.optionSorter)
      : normalized
    return options.map((opt) => (
      <option
        disabled={opt.disabled}
        key={`option-${opt.value || shortId()}`}
        value={opt.value}
      >
        {opt.label}
      </option>
    ))
  }

  static getFilterComponent(filter: FilterConfig, columnKey: string) {
    let filterComponent
    const clear = filter.clear || undefined
    switch (filter.type.toLowerCase()) {
      case 'select':
        filterComponent = (
          <div
            className={classnames('filter-container', styles.filterContainer)}
          >
            <select
              autoComplete='off'
              className={classnames('filter', styles.filter)}
              data-column-key={columnKey}
              {...omit(filter, ['options', 'placeholder', 'type', 'clear'])}
            >
              {ColumnHeader.makeOptions(filter.options || [])}
            </select>
          </div>
        )
        break
      case 'input':
        filterComponent = (
          <div
            className={classnames('filter-container', styles.filterContainer)}
          >
            <input
              autoComplete='off'
              className={classnames('filter', styles.filter)}
              type='text'
              data-column-key={columnKey}
              {...omit(filter, ['type', 'options', 'clear'])}
            />
            {clear && filter.value && (
              <button
                onClick={() => clear(columnKey)}
                className={styles.clearFilter}
              >
                <Close />
              </button>
            )}
          </div>
        )
        break
      default:
        // ignore
        break
    }
    return filterComponent
  }

  render() {
    const {
      className,
      columnKey,
      dragHandleIcon,
      filter,
      resizable,
      onResizeStart,
      onResizeEnd,
      label,
      onLabelClick,
      resizeBounds,
      columnLabelStyle,
      style,
      sortIcon
    } = this.props

    const dragHandleProps: ColumnDragHandleProps = {
      bounds: resizeBounds,
      columnKey,
      onDragStart: onResizeStart,
      onDragStop: onResizeEnd
    }

    if (dragHandleIcon) {
      dragHandleProps.dragHandleIcon = dragHandleIcon
    }

    let filterComponent

    if (filter) {
      filterComponent = ColumnHeader.getFilterComponent(filter, columnKey)
    }

    return (
      <div
        className={classnames(
          'column-header-container',
          className,
          styles.columnHeaderContainer
        )}
        style={style}
      >
        <div
          className={classnames('column-header', styles.columnHeader)}
          key='label'
          title={label}
        >
          {/* eslint-disable jsx-a11y/anchor-is-valid */}
          <div className={classnames('label-row', styles.labelRow)}>
            <a
              className={classnames(
                'column-header-label',
                this.props.className
              )}
              role='presentation'
              data-column-key={columnKey}
              onClick={onLabelClick}
              style={columnLabelStyle}
            >
              {/* eslint-enable jsx-a11y/anchor-is-valid */}
              {label}
            </a>
            {sortIcon && (
              <span className={`sort-icon ${styles.sortIcon}`}>{sortIcon}</span>
            )}
          </div>
          {filterComponent}
        </div>
        {resizable && <ColumnDragHandle {...dragHandleProps} />}
      </div>
    )
  }
}

export default ColumnHeader
